
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useServerTime } from './ServerTimeContext';
import { getDashboardAccountEmail, getDashboardAccountId, getDashboardAccountName, getDashboardAccountRole, getDashboardAccountUserId, setDashboardAccountState } from './accountStorage';

// --- Types ---
export interface Event {
  id: string;
  name: string;
  title?: string;
  date: string;
  ticketsSold: number;
  capacity: number;
  status: 'Live' | 'Draft' | 'Ended' | 'In Planung' | 'Cancelled';
  revenue: number;
  genre: string[];
  location?: string;
  recurring?: boolean;
  recurrenceLabel?: string;
  image?: string;
  archived?: boolean;
  startDateISO?: string | null;
  endDateISO?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  // ticketing metadata (optional)
  ticketingType?: string;
  price?: number;
  categories?: any[];
  // moderation flags
  banned?: boolean;
  banReason?: string;
}

export interface Deal {
  id: string;
  title: string;
  description: string;
  active: boolean;
  type: 'drink' | 'entry';
  redeemed: number;
  eventId?: string;
  eventTitle?: string;
  startAt?: string | null;
  endAt?: string | null;
}

export interface Ride {
  id: string;
  passenger: string;
  destination: string;
  price: number;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  distance: string;
}

export type EventType = 'Nicht definiert' | 'Club' | 'Konzert' | 'Festival' | 'Rave' | 'Open Air' | 'Studentenparty' | 'Dorfparty' | 'Bar Event' | 'Networking Event' | 'Beach Party' | 'Silent Disco' | 'Special Event';

export interface Location {
  id: string;
  name: string;
  type?: EventType;
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  country?: string;
  capacity?: number;
  notes?: string;
}

interface DashboardContextType {
  events: Event[];
  eventsLoading: boolean;
  archivedEvents: Event[];
  pulsePosts: any[];
  fetchPulsePosts: () => Promise<void>;
  addPulsePost: (content: string, image?: string, category?: string, tags?: string[]) => Promise<void>;
  deletePulsePost: (id: string) => Promise<void>;
  pulseProfile: any;
  fetchPulseProfile: () => Promise<any>;
  updatePulseProfile: (patch: any) => Promise<any>;
  addEvent: (event: Omit<Event, 'ticketsSold' | 'revenue'> & { id?: string }) => void;
  removeEvent: (id: string) => void;
  deals: Deal[];
  addDeal: (deal: Omit<Deal, 'id' | 'redeemed'>) => void;
  toggleDeal: (id: string) => void;
  updateDeal: (id: string, patch: Partial<Deal>) => Promise<void>;
  deleteDeal: (id: string) => Promise<void>;
  activeRide: Ride | null;
  incomingRide: Ride | null;
  driverStatus: 'online' | 'offline' | 'busy';
  setDriverStatus: (status: 'online' | 'offline' | 'busy') => void;
  acceptRide: () => void;
  completeRide: () => void;
  notifications: string[];
  addNotification: (msg: string) => void;
  locations: Location[];
  addLocation: (loc: Omit<Location, 'id'>) => void;
  updateLocation: (id: string, patch: Partial<Location>) => void;
  removeLocation: (id: string) => void;
  updateEvent: (id: string, patch: Partial<Event>) => void;
  models: any[];
  currentModel: any | null;
  fetchModels: () => Promise<void>;
  hasFeature: (featureKey: string) => boolean;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

const BAR_MODEL_FEATURES: Record<string, string[]> = {
  free: ['pulse'],
  basic: ['listing', 'pulse', 'appEditor', 'stats', 'drinksDeals', 'liveMap'],
  standard: ['listing', 'pulse', 'appEditor', 'stats', 'drinksDeals', 'liveMap', 'inAppBooking', 'eventAutomation', 'happyHour', 'groupOffers', 'barPush'],
  premium: ['listing', 'pulse', 'appEditor', 'stats', 'drinksDeals', 'liveMap', 'inAppBooking', 'eventAutomation', 'happyHour', 'groupOffers', 'barPush', 'secretMenu', 'premiumList', 'localSuggestions', 'giveaways', 'ticketing'],
};

const buildFallbackModels = () => ([
  { id: 'free', name: 'Free', price: 0, features: BAR_MODEL_FEATURES.free },
  { id: 'basic', name: 'Basic', price: 29, features: BAR_MODEL_FEATURES.basic },
  { id: 'standard', name: 'Standard', price: 79, features: BAR_MODEL_FEATURES.standard },
  { id: 'premium', name: 'Premium', price: 149, features: BAR_MODEL_FEATURES.premium },
]);

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) throw new Error('useDashboard must be used within a DashboardProvider');
  return context;
};

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { now: serverNow } = useServerTime();
  // Helper: prefer authoritative activeAccount* keys, fall back to legacy account* keys
  const getAccountId = () => {
    return getDashboardAccountId();
  };
  const getAccountEmail = () => {
    return getDashboardAccountEmail();
  };
  const getAccountName = () => {
    return getDashboardAccountName();
  };
  const getAccountUserId = () => {
    return getDashboardAccountUserId();
  };
  const persistAccountIdentity = (nextId?: string | null, nextEmail?: string | null, nextName?: string | null, nextUserId?: string | null) => {
    const role = getDashboardAccountRole();
    if (!nextId && !nextEmail && !nextName && !nextUserId) return;
    setDashboardAccountState(role, {
      id: nextId || null,
      email: nextEmail || null,
      name: nextName || null,
      userId: nextUserId || null,
    });
  };
  // --- Mock Data State ---
  // Start with no events by default; live fetch will populate when available
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [archivedEvents, setArchivedEvents] = useState<Event[]>([]);

  const [deals, setDeals] = useState<Deal[]>([]);

  const [driverStatus, setDriverStatus] = useState<'online' | 'offline' | 'busy'>('offline');
  const [activeRide, setActiveRide] = useState<Ride | null>(null);
  const [incomingRide, setIncomingRide] = useState<Ride | null>(null);
  const [notifications, setNotifications] = useState<string[]>([]);

  const [locations, setLocations] = useState<Location[]>(() => {
    try {
      if (typeof window === 'undefined') return [];
      const accountId = getAccountId() || 'anon';
      const raw = localStorage.getItem(`locations:${accountId}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((loc: any) => ({
        id: String(loc.id || loc._id || Math.random().toString()),
        name: String(loc.name || loc.clubName || ''),
        type: loc.type || '',
        street: loc.street || '',
        houseNumber: loc.houseNumber || '',
        zip: loc.zip || '',
        city: loc.city || '',
        country: loc.country || '',
        capacity: loc.capacity || undefined,
        notes: loc.notes || ''
      }));
    } catch (e) {
      return [];
    }
  });

  const getLocationsKey = () => {
    if (typeof window === 'undefined') return 'locations:anon';
    const accountId = getAccountId() || 'anon';
    return `locations:${accountId}`;
  };

  const persistLocations = (next: Location[]) => {
    try { localStorage.setItem(getLocationsKey(), JSON.stringify(next)); } catch (e) { /* ignore */ }
  };
  // Feature models state (Free/Basic/Standard/Premium)
  const [models, setModels] = useState<any[]>(() => buildFallbackModels());
  const [currentModel, setCurrentModel] = useState<any | null>(() => buildFallbackModels()[0]);
  const fetchModels = async () => {
    try {
      const accountId = getAccountId();
      const url = accountId ? `/api/bar/models?organizerId=${encodeURIComponent(accountId)}` : '/api/bar/models';
      const res = await fetch(url, { credentials: 'include' }).catch(() => null);
      const j = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && j && Array.isArray(j.models)) {
        setModels(j.models);
        setCurrentModel(j.current || j.models.find((m: any) => m.id === 'free') || buildFallbackModels()[0]);
        return;
      }
      // fallback: ask server for basic model list without features
      if (j && Array.isArray(j.models)) setModels(j.models);
    } catch (e) { console.error('[dashboard] fetchModels failed', e); }
  };

  const hasFeature = (featureKey: string) => {
    try {
      if (!currentModel || !Array.isArray(currentModel.features)) return false;
      // Admin pulse block overrides pulse feature
      if (featureKey === 'pulse' && pulseProfile && pulseProfile.pulseBlocked) return false;
      return currentModel.features.includes(featureKey);
    } catch (e) { return false; }
  };
  // Load models on mount
  useEffect(() => { fetchModels(); }, []);
  // Pulse posts and organizer pulse profile
  const [pulsePosts, setPulsePosts] = useState<Array<any>>([]);
  const [pulseProfile, setPulseProfile] = useState<any>(null);

  const profileHasContent = (p: any, accountName?: string | null) => {
    if (!p) return false;
    const keys = ['handle', 'avatarUrl', 'bio', 'location', 'website', 'instagram', 'type', 'bannerImage'];
    if (keys.some(k => !!(p[k]))) return true;
    // If displayName exists but differs from accountName, treat as real
    if (p.displayName && accountName && String(p.displayName).trim() !== String(accountName).trim()) return true;
    return false;
  };
  const addLocation = async (loc: Omit<Location, 'id'>) => {
    try {
      const accountId = getAccountId();
      const accountEmail = getAccountEmail();
      const accountName = getAccountName();
      const body: any = { ...loc };
      if (accountId) body.organizerId = accountId;
      else if (accountEmail) body.email = accountEmail;
      else if (accountName) body.name = accountName;
      const res = await fetch('/api/organizer/locations', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await res.json();
      if (res.ok && j && j.ok && j.location) {
        const created: Location = { id: String(j.location.id), name: String(j.location.clubName || j.location.name || ''), street: j.location.street || '', houseNumber: j.location.houseNumber || '', zip: j.location.zip || '', city: j.location.city || '', country: j.location.country || '', type: j.location.type || '', capacity: j.location.capacity || undefined, notes: j.location.notes || '' };
        setLocations(prev => { const next = [...prev, created]; persistLocations(next); return next; });
        try { if (typeof window !== 'undefined') window.dispatchEvent(new Event('account-changed')); } catch (e) {}
      }
    } catch (e) { console.error('[dashboard] addLocation failed', e); }
  };

  const fetchPulsePosts = async () => {
    try {
      const accountId = getAccountId();
      const accountEmail = getAccountEmail();
      let url = '';
      if (accountId) url = `/api/pulse?organizerId=${encodeURIComponent(accountId)}`;
      else if (accountEmail) url = `/api/pulse?email=${encodeURIComponent(accountEmail || '')}`;
      if (!url) return;
      const res = await fetch(url, { credentials: 'include' });
      const j = await res.json().catch(() => null);
      if (res.ok && j && j.ok && Array.isArray(j.posts)) {
        setPulsePosts(j.posts.map((p: any) => ({ ...p, id: String(p._id || p.id) })));
      } else {
        setPulsePosts([]);
      }
    } catch (e) { console.error('[dashboard] fetchPulsePosts failed', e); }
  };

  const addPulsePost = async (content: string, image?: string, category?: string, tags?: string[]) => {
    try {
      if (!hasFeature('pulse')) {
        console.warn('[dashboard] addPulsePost prevented: pulse feature not enabled or account blocked');
        return;
      }
      const accountId = getAccountId();
      const accountEmail = getAccountEmail();
      const body: any = { content, image: image || '', category: category || 'General' };
      if (tags && Array.isArray(tags)) body.tags = tags;
      if (accountId) body.organizerId = accountId; else if (accountEmail) body.email = accountEmail;
      const res = await fetch('/api/pulse', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) }).catch(() => null);
      const j = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && j && j.ok && j.post) {
        setPulsePosts(prev => [{ ...j.post, id: String(j.post._id || j.post.id) }, ...prev]);
      } else {
        try { console.warn('[dashboard] addPulsePost rejected', j && j.error ? j.error : 'no response'); } catch(e){}
      }
    } catch (e) { console.error('[dashboard] addPulsePost failed', e); }
  };

  const deletePulsePost = async (id: string) => {
    try {
      const res = await fetch(`/api/pulse/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }).catch(() => null);
      if (res && res.ok) setPulsePosts(prev => prev.filter(p => String(p.id) !== String(id)));
    } catch (e) { console.error('[dashboard] deletePulsePost failed', e); }
  };

  const updatePulseProfile = async (patch: any) => {
    try {
      let accountId = getAccountId();
      const accountEmail = getAccountEmail();
      let accountName = getAccountName();
      const body: any = { ...patch };

      // If we don't have any account identifier, try to create a minimal organizer account first
      if (!accountId && !accountEmail && !accountName) {
        // prefer a provided name in patch (pulseProfiles[0].displayName) or fallback to localStorage
        const candidateName = (patch && patch.name) || (patch && patch.pulseProfiles && Array.isArray(patch.pulseProfiles) && patch.pulseProfiles[0] && patch.pulseProfiles[0].displayName) || accountName || null;
        if (candidateName) {
          try {
            const crt = await fetch('/api/organizer/create', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: candidateName, email: accountEmail || '' }) });
            const cj = await crt.json().catch(() => null);
            if (crt.ok && cj && cj.ok && cj.organizerId) {
              accountId = String(cj.organizerId);
              try { localStorage.setItem('accountId', accountId); } catch (e) {}
            }
          } catch (e) { /* ignore create errors */ }
        }
      }
      if (accountId) body.organizerId = accountId;
      else if (accountEmail) body.email = accountEmail;
      else if (accountName) body.name = accountName;

      const res = await fetch('/api/organizer/profile', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) }).catch(() => null);
      const j = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && j && j.ok) {
        if (j.organizerId) persistAccountIdentity(String(j.organizerId), accountEmail || null, accountName || null, null);
        // server may return pulseProfiles array for multiple profiles — pick first as active
        const base = (j.pulseProfiles && Array.isArray(j.pulseProfiles) && j.pulseProfiles.length) ? j.pulseProfiles[0] : (j.profile || null);
        if (base) {
          let nextProfile = Object.assign({}, base, { organizerId: j.organizerId || base.organizerId || null, stripeAccountId: j.stripeAccountId || base.stripeAccountId || null, pulseBlocked: !!j.pulseBlocked, pulseBlockReason: j.pulseBlockReason || '' });
          if (nextProfile.organizerId && nextProfile.stripeAccountId) {
            try {
              const stripeRes = await fetch(`/api/stripe/account?organizerId=${encodeURIComponent(String(nextProfile.organizerId))}`, { credentials: 'include' }).catch(() => null);
              const stripeJson = stripeRes ? await stripeRes.json().catch(() => null) : null;
              if (stripeRes && stripeRes.ok && stripeJson) {
                nextProfile = Object.assign(nextProfile, {
                  stripeAccountStatus: stripeJson.stripeAccountStatus || null,
                  stripeAccount: stripeJson.stripeAccount || null,
                });
              }
            } catch (e) { /* ignore */ }
          }
          setPulseProfile(nextProfile);
        }
        else setPulseProfile(null);
        if (j.organizerId) persistAccountIdentity(String(j.organizerId), accountEmail || null, accountName || null, null);
        try { if (typeof window !== 'undefined') window.dispatchEvent(new Event('account-changed')); } catch (e) {}
        return j;
      }
    } catch (e) { console.error('[dashboard] updatePulseProfile failed', e); }
    return null;
  };

    const fetchPulseProfile = async () => {
      try {
        const accountId = getAccountId();
        const accountEmail = getAccountEmail();
        let profUrl = '/api/organizer/profile';
        if (accountId) profUrl += `?organizerId=${encodeURIComponent(accountId)}`;
        else if (accountEmail) profUrl += `?email=${encodeURIComponent(accountEmail || '')}`;
        const res = await fetch(profUrl, { credentials: 'include' }).catch(() => null);
        const j = res ? await res.json().catch(() => null) : null;
        if (res && res.ok && j) {
          const base = (j.pulseProfiles && Array.isArray(j.pulseProfiles) && j.pulseProfiles.length) ? j.pulseProfiles[0] : (j.profile || null);
          if (base) {
            let nextProfile = Object.assign({}, base, { organizerId: j.organizerId || base.organizerId || null, stripeAccountId: j.stripeAccountId || base.stripeAccountId || null, pulseBlocked: !!j.pulseBlocked, pulseBlockReason: j.pulseBlockReason || '' });
            if (nextProfile.organizerId && nextProfile.stripeAccountId) {
              try {
                const stripeRes = await fetch(`/api/stripe/account?organizerId=${encodeURIComponent(String(nextProfile.organizerId))}`, { credentials: 'include' }).catch(() => null);
                const stripeJson = stripeRes ? await stripeRes.json().catch(() => null) : null;
                if (stripeRes && stripeRes.ok && stripeJson) {
                  nextProfile = Object.assign(nextProfile, {
                    stripeAccountStatus: stripeJson.stripeAccountStatus || null,
                    stripeAccount: stripeJson.stripeAccount || null,
                  });
                }
              } catch (e) { /* ignore */ }
            }
            setPulseProfile(nextProfile);
          }
          else setPulseProfile(null);
          return j;
        }
      } catch (e) { console.error('[dashboard] fetchPulseProfile failed', e); }
      return null;
    };

  const updateLocation = async (id: string, patch: Partial<Location>) => {
    try {
      const res = await fetch(`/api/organizer/locations/${encodeURIComponent(id)}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      const j = await res.json();
      if (res.ok && j && j.ok && j.location) {
        setLocations(prev => { const next = prev.map(l => l.id === id ? { ...l, ...patch, name: String(j.location.clubName || j.location.name || (l.name || '')) } : l); persistLocations(next); return next; });
      }
    } catch (e) { console.error('[dashboard] updateLocation failed', e); }
  };

  const removeLocation = async (id: string) => {
    try {
      const res = await fetch(`/api/organizer/locations/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setLocations(prev => { const next = prev.filter(l => l.id !== id); persistLocations(next); return next; });
      }
    } catch (e) { console.error('[dashboard] removeLocation failed', e); }
  };

  const hasServerTime = serverNow !== null;

  // Track active account key so organizer/profile polling can react to account changes.
  const [activeAccountKey, setActiveAccountKey] = useState<string | null>(() => {
      if (typeof window === 'undefined') return null;
      try {
        return sessionStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountEmail') || sessionStorage.getItem('activeAccountName') || localStorage.getItem('accountId') || localStorage.getItem('accountEmail') || localStorage.getItem('accountName') || localStorage.getItem('userName') || localStorage.getItem('name') || null;
      } catch (e) { return null; }
  });

  const [accountRefreshTick, setAccountRefreshTick] = useState(0);

  const readActiveAccountKey = () => {
    if (typeof window === 'undefined') return null;
    try {
      return sessionStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountEmail') || sessionStorage.getItem('activeAccountName') || localStorage.getItem('accountId') || localStorage.getItem('accountEmail') || localStorage.getItem('accountName') || localStorage.getItem('userName') || localStorage.getItem('name') || null;
    } catch (e) {
      return null;
    }
  };

  // Listen for storage changes (account switch in another tab) and update key.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncActiveAccountKey = () => {
      const newKey = readActiveAccountKey();
      setActiveAccountKey(prev => (prev === newKey ? prev : newKey));
    };
    const onStorage = (ev: StorageEvent) => {
      try {
        setAccountRefreshTick(prev => prev + 1);
        syncActiveAccountKey();
      } catch (e) { /* ignore */ }
    };
    const onAccountChanged = () => {
      setAccountRefreshTick(prev => prev + 1);
      syncActiveAccountKey();
    };
    const onFocus = () => {
      setAccountRefreshTick(prev => prev + 1);
      syncActiveAccountKey();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') { setAccountRefreshTick(prev => prev + 1); syncActiveAccountKey(); } };
    syncActiveAccountKey();
    window.addEventListener('storage', onStorage);
    window.addEventListener('account-changed', onAccountChanged as EventListener);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('account-changed', onAccountChanged as EventListener);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Simulate incoming ride requests when online
  useEffect(() => {
    if (driverStatus === 'online' && !activeRide && !incomingRide) {
      const timer = setTimeout(() => {
        setIncomingRide({
          id: Math.random().toString(),
          passenger: 'Max Mustermann',
          destination: 'Karl-Liebknecht-Str. 85',
          price: 14.50,
          status: 'pending',
          distance: '2.5 km'
        });
        addNotification('Neue Fahrtanfrage verfügbar!');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [driverStatus, activeRide, incomingRide]);

  // Fetch live organizer events using the authenticated session cookies.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasServerTime) return;

    let mounted = true;

    (async () => {
      try {
        const accountId = sessionStorage.getItem('activeAccountId') || localStorage.getItem('accountId');
        const accountEmail = sessionStorage.getItem('activeAccountEmail') || localStorage.getItem('accountEmail');
        const accountName = sessionStorage.getItem('activeAccountName') || localStorage.getItem('accountName');
        if (!accountId && !accountEmail && !accountName) return;

        const eventsParams = new URLSearchParams();
        if (accountId) eventsParams.set('organizerId', accountId);
        if (accountEmail) eventsParams.set('email', accountEmail);
        if (accountName) eventsParams.set('name', accountName);
        const eventsUrl = `/api/organizer/events?${eventsParams.toString()}`;

        setEventsLoading(true);

        const res = await fetch(eventsUrl, { credentials: 'include' });
        const j = await res.json().catch(() => null);
        if (!mounted) return;

        if (res.ok && j && j.ok && Array.isArray(j.events)) {
          const mapped = j.events.map((ev: any) => ({
            id: String(ev.id || ev._id || ''),
            name: ev.name || '',
            date: ev.date || '',
            ticketsSold: Number(ev.ticketsSold || 0),
            capacity: Number(ev.capacity || 0),
            status: (ev.status === 'Live' ? 'Live' : (ev.status === 'Draft' ? 'Draft' : 'In Planung')) as any,
            revenue: Number(ev.revenue || 0),
            genre: ev.genre || [],
            location: ev.location || '',
            recurring: !!ev.recurring,
            recurrenceLabel: ev.recurrenceLabel || '',
            image: ev.image || '/images/event-placeholder.svg',
            banned: !!ev.banned,
            banReason: ev.banReason || '',
            ticketingType: ev.ticketingType || ev.ticketing || 'fixed',
            price: Number(ev.price || 0),
            categories: Array.isArray(ev.categories) ? ev.categories : (Array.isArray(ev.tickets) ? ev.tickets : []),
            locationType: ev.locationType || ''
          }));

          const now = serverNow === null ? null : new Date(serverNow);
          const withMeta: Event[] = mapped.map((m: any) => {
            const raw = j.events.find((e: any) => String(e.id || e._id || '') === String(m.id)) || {};
            const startISO = raw.startDateISO || raw.startDate || (raw.recurrence && raw.recurrence.start ? raw.recurrence.start : null) || null;
            const endISO = (raw.recurrence && raw.recurrence.end) || raw.endDateISO || raw.endDate || null;
            let archived = false;
            try {
              const start = startISO ? new Date(startISO) : null;
              const end = endISO ? new Date(endISO) : null;
              if (m.recurring) {
                // recurring events should stay visible as a bundled series on the live side;
                // individual past occurrences are archived separately by the API.
                archived = false;
              } else {
                if (end && !isNaN(end.getTime()) && now && end < now) archived = true;
                else if (start && !isNaN(start.getTime()) && now && start < now) archived = true;
              }
            } catch (err) {
              archived = false;
            }
            return { ...m, archived, startDateISO: startISO, endDateISO: endISO, startDate: startISO, endDate: endISO } as Event;
          });

          const nextEvents = withMeta.filter(e => !e.archived);
          const nextArchived = withMeta.filter(e => !!e.archived);
          if (nextEvents.length > 0 || nextArchived.length > 0) {
            setEvents(nextEvents);
            setArchivedEvents(nextArchived);
          }
        } else {
          // Keep previously rendered events if the refresh returns empty.
          try { console.warn('[dashboard] organizer events refresh returned empty payload, keeping existing list'); } catch (e) {}
        }
      } catch (err) {
        console.error('[dashboard] fetch organizer events failed', err);
        if (mounted) {
          // Keep existing list on transient fetch errors.
        }
      } finally {
        if (mounted) setEventsLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [activeAccountKey, accountRefreshTick, hasServerTime]);

  // Poll organizer profile periodically while dashboard is visible to pick up remote changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let mounted = true;
    let intervalId: any = null;
    const accountId = sessionStorage.getItem('activeAccountId') || localStorage.getItem('accountId');
    const accountEmail = sessionStorage.getItem('activeAccountEmail') || localStorage.getItem('accountEmail');
    const accountName = sessionStorage.getItem('activeAccountName') || localStorage.getItem('accountName') || localStorage.getItem('userName') || localStorage.getItem('name');
    // If this is a guest login, do not poll organizer profile
    const guestFlag = (sessionStorage.getItem('guestLoggedIn') || localStorage.getItem('guestLoggedIn'));
    if (guestFlag) return;

    const shouldFetch = () => {
      if (typeof window !== 'undefined' && window.location && window.location.pathname === '/debug-file-input') return false;
      return document.visibilityState === 'visible';
    };

    const mapLocations = (rawLocations: any[]) => rawLocations.map((l: any) => ({
      id: String(l.id || l._id || ''),
      name: String(l.clubName || l.name || ''),
      street: l.street || '',
      houseNumber: l.houseNumber || '',
      zip: l.zip || '',
      city: l.city || '',
      type: l.type || '',
      country: l.country || '',
      capacity: l.capacity || undefined,
      notes: l.notes || ''
    }));

    const fetchLocationsFallback = async () => {
      try {
        let locUrl = '';
        if (accountId) locUrl = `/api/organizer/locations?organizerId=${encodeURIComponent(accountId)}`;
        else if (accountEmail) locUrl = `/api/organizer/locations?email=${encodeURIComponent(accountEmail || '')}`;
        else if (accountName) locUrl = `/api/organizer/locations?name=${encodeURIComponent(accountName || '')}`;
        if (!locUrl) locUrl = '/api/organizer/locations';
        try { console.info('[dashboard] locations fallback fetch start', { locUrl }); } catch (e) {}
        const lres = await fetch(locUrl, { credentials: 'include' }).catch(() => null);
        const lj = lres ? await lres.json().catch(() => null) : null;
        try { console.info('[dashboard] locations fallback fetch done', { ok: !!(lres && lres.ok), status: lres ? lres.status : null, hasLocations: !!(lj && Array.isArray(lj.locations)), count: lj && Array.isArray(lj.locations) ? lj.locations.length : 0 }); } catch (e) {}
        if (lres && lres.ok && lj && Array.isArray(lj.locations)) {
          const fallbackLocs = mapLocations(lj.locations);
          try { console.info('[dashboard] locations loaded from locations endpoint', { organizer: accountName || accountEmail || accountId || 'unknown', count: fallbackLocs.length, locations: fallbackLocs }); } catch (e) {}
          setLocations(fallbackLocs);
          persistLocations(fallbackLocs);
        }
      } catch (e) { /* ignore */ }
    };

    const fetchProfile = async () => {
      try {
        if (!shouldFetch()) return;
        let profUrl = '';
        if (accountId) profUrl = `/api/organizer/profile?organizerId=${encodeURIComponent(accountId)}`;
        else if (accountEmail) profUrl = `/api/organizer/profile?email=${encodeURIComponent(accountEmail || '')}`;
        else if (accountName) profUrl = `/api/organizer/profile?name=${encodeURIComponent(accountName || '')}`;
        if (!profUrl) profUrl = '/api/organizer/profile';
        try { console.info('[dashboard] profile fetch start', { profUrl }); } catch (e) {}
        const prf = await fetch(profUrl, { credentials: 'include' }).catch(() => null);
        const pjf = prf ? await prf.json().catch(() => null) : null;
        try { console.info('[dashboard] profile fetch done', { ok: !!(prf && prf.ok), status: prf ? prf.status : null, hasLocations: !!(pjf && Array.isArray(pjf.locations)), count: pjf && Array.isArray(pjf.locations) ? pjf.locations.length : 0 }); } catch (e) {}
        if (!(prf && prf.ok && pjf && pjf.ok)) {
          await fetchLocationsFallback();
          return;
        }
        if (prf && prf.ok && pjf && pjf.ok) {
          if (pjf.organizerId) persistAccountIdentity(String(pjf.organizerId), accountEmail || null, accountName || null, pjf.userId || null);
          const accountNameLocal = getAccountName();
          const prof = pjf.profile || null;
          // Build canonical next profile object.
          // IMPORTANT: include organizerId + stripeAccountId from the top-level response
          // (they live on `pjf`, not inside `pjf.profile`). Otherwise this polling loop
          // overwrites the enriched profile from fetchPulseProfile and the dashboard loses
          // the Stripe connection state on every refresh (-> spurious account_missing).
          const next = Object.assign({}, (prof || {}), {
            organizerId: pjf.organizerId || (prof && prof.organizerId) || null,
            stripeAccountId: pjf.stripeAccountId || (prof && prof.stripeAccountId) || null,
            pulseBlocked: !!pjf.pulseBlocked, pulseBlockReason: pjf.pulseBlockReason || '', banned: !!pjf.banned, banReason: pjf.banReason || ''
          });
          // Enrich with live Stripe status so feature gating (event creation etc.) works
          // immediately after a reload without waiting for a manual fetchPulseProfile().
          if (next.organizerId && next.stripeAccountId) {
            try {
              const stripeRes = await fetch(`/api/stripe/account?organizerId=${encodeURIComponent(String(next.organizerId))}`, { credentials: 'include' }).catch(() => null);
              const stripeJson = stripeRes ? await stripeRes.json().catch(() => null) : null;
              if (stripeRes && stripeRes.ok && stripeJson) {
                next.stripeAccountStatus = stripeJson.stripeAccountStatus || null;
                next.stripeAccount = stripeJson.stripeAccount || null;
              }
            } catch (e) { /* ignore */ }
          }
          if (Array.isArray(pjf.locations) && pjf.locations.length > 0) {
            const mappedLocs = mapLocations(pjf.locations);
            try { console.info('[dashboard] locations loaded from profile', { organizer: accountNameLocal || getAccountName() || 'unknown', count: mappedLocs.length, locations: mappedLocs }); } catch (e) {}
            setLocations(mappedLocs);
            persistLocations(mappedLocs);
          } else {
            await fetchLocationsFallback();
          }

          // Compare shallowly to avoid unnecessary setState and re-renders
          setPulseProfile((prev: any) => {
            try {
              const pick = (p: any) => ({ displayName: p?.displayName || '', handle: (p?.handle || '').replace(/^@+/, ''), avatarUrl: p?.avatarUrl || p?.avatar || '', bio: p?.bio || '', location: p?.location || '', website: p?.website || '', instagram: p?.instagram || '', pulseBlocked: !!p?.pulseBlocked, banned: !!p?.banned, organizerId: p?.organizerId || '', stripeAccountId: p?.stripeAccountId || '', stripeAccountStatus: p?.stripeAccountStatus || '' });
              const a = pick(prev || { displayName: accountNameLocal || '' });
              const b = pick(next || {});
              const aJson = JSON.stringify(a);
              const bJson = JSON.stringify(b);
              if (aJson === bJson) return prev || a; // no change
              // merge previous (preserve existing fields) with next
              const merged = Object.assign({}, prev || {}, next || {});
              return merged;
            } catch (e) {
              return next;
            }
          });
        }
      } catch (e) { /* ignore transient errors */ }
    };

    // initial fetch and then interval
    fetchProfile();
    intervalId = setInterval(fetchProfile, 20000);

    const onVis = () => { if (shouldFetch()) fetchProfile(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [activeAccountKey, accountRefreshTick]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const accountId = sessionStorage.getItem('activeAccountId') || localStorage.getItem('accountId');
    const accountEmail = sessionStorage.getItem('activeAccountEmail') || localStorage.getItem('accountEmail');
    const accountName = sessionStorage.getItem('activeAccountName') || localStorage.getItem('accountName') || localStorage.getItem('userName') || localStorage.getItem('name');
    if (!accountId && !accountEmail && !accountName) return;

    const eventsParams = new URLSearchParams();
    if (accountId) eventsParams.set('organizerId', accountId);
    if (accountEmail) eventsParams.set('email', accountEmail || '');
    if (accountName) eventsParams.set('name', accountName || '');
    const eventsUrl = `/api/organizer/events?${eventsParams.toString()}`;
    if (!eventsUrl) return;

    let cancelled = false;
    (async () => {
      try {
        try { console.info('[dashboard] events bootstrap fetch start', { eventsUrl }); } catch (e) {}
        const res = await fetch(eventsUrl, { credentials: 'include' }).catch(() => null);
        const json = res ? await res.json().catch(() => null) : null;
        try { console.info('[dashboard] events bootstrap fetch done', { ok: !!(res && res.ok), status: res ? res.status : null, count: json && Array.isArray(json.events) ? json.events.length : 0 }); } catch (e) {}
        if (cancelled) return;
        if (res && res.ok && json && Array.isArray(json.events)) {
          const mappedEvents = json.events.map((e: any) => ({
            id: String(e.id || e._id || ''),
            name: String(e.name || e.title || ''),
            location: String(e.location || ''),
            date: String(e.date || ''),
            ticketsSold: Number(e.ticketsSold || e.tickets || 0),
            status: String(e.status || 'Draft'),
            banned: !!e.banned,
            banReason: e.banReason || '',
            recurring: !!e.recurring,
            recurrenceLabel: e.recurrenceLabel || '',
            image: e.image || e.bannerImage || '/images/event-placeholder.svg',
            startDateISO: e.startDateISO || null,
            endDateISO: e.endDateISO || null,
            startTime: e.startTime || '',
            endTime: e.endTime || '',
            ticketingType: e.ticketingType || 'fixed',
            price: e.price || 0,
            categories: Array.isArray(e.categories) ? e.categories : [],
          }));
          setEvents(mappedEvents as any);
          setArchivedEvents([]);
        }
      } catch (e) { /* ignore */ }
    })();

    return () => { cancelled = true; };
  }, [activeAccountKey]);

  // Fetch live deals for organizer when active account changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const accountId = sessionStorage.getItem('activeAccountId') || localStorage.getItem('accountId');
    const accountEmail = sessionStorage.getItem('activeAccountEmail') || localStorage.getItem('accountEmail');

    // clear previous deals immediately to avoid showing wrong data briefly
    setDeals([]);

    if (!accountId && !accountEmail) return;
    (async () => {
      try {
        let url = '';
        if (accountId) url = `/api/organizer/deals?organizerId=${encodeURIComponent(accountId)}`;
        else if (accountEmail) url = `/api/organizer/deals?email=${encodeURIComponent(accountEmail || '')}`;
        if (!url) return;
        const res = await fetch(url);
        const j = await res.json().catch(() => null);
        if (res.ok && j && j.ok && Array.isArray(j.deals)) {
          const mapped = j.deals.map((d: any) => ({ id: String(d._id || d.id), title: d.title || '', description: d.description || '', active: !!d.active, type: d.type || 'drink', redeemed: Number(d.redeemed || 0), startAt: d.startAt ? (new Date(d.startAt)).toISOString() : null, endAt: d.endAt ? (new Date(d.endAt)).toISOString() : null }));
          setDeals(mapped);
        }
      } catch (e) { console.error('[dashboard] fetch deals failed', e); }
    })();
  }, [activeAccountKey]);

  // --- Actions ---
  const addEvent = (event: Omit<Event, 'ticketsSold' | 'revenue'> & { id?: string }) => {
    const newEvent: Event = { ...event, id: event.id ? String(event.id) : Math.random().toString(), ticketsSold: 0, revenue: 0 } as Event;
    setEvents(prev => [...prev, newEvent]);
  };

  const removeEvent = (id: string) => {
    setEvents(prev => prev.filter(e => e.id !== id));
    addNotification('Event gelöscht.');
    // Attempt to delete on server in background (best-effort)
    try {
      if (typeof window !== 'undefined') {
        fetch(`/api/organizer/events/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(r => {
          if (!r.ok) console.warn('[dashboard] server delete returned', r.status);
        }).catch(err => console.warn('[dashboard] delete event failed', err));
      }
    } catch (e) { /* ignore */ }
  };
  
  const updateEvent = (id: string, patch: Partial<Event>) => {
    // Prevent local status changes when the event is banned by Master
    try {
      const existing = events.find(e => String(e.id) === String(id)) as any;
      if (existing && existing.banned && (patch.status !== undefined || (patch as any).published !== undefined)) {
        try { alert('Event ist vom Master gesperrt und kann nicht bearbeitet werden.'); } catch (e) {}
        console.warn('[dashboard] updateEvent prevented for banned event', id);
        return;
      }
    } catch (err) { /* ignore */ }

    // If the patch contains a status change, attempt to persist it to the server
    (async () => {
      try {
        const accountId = getDashboardAccountId();
        // map UI status to server payload (server expects `publish` boolean)
        const body: any = {};
        if (patch.status !== undefined) {
          if (patch.status === 'Live') body.publish = true;
          else body.publish = false;
        }
        // include organizerId when available so server can verify ownership
        if (accountId) body.organizerId = accountId;

        // only call server if we have something to persist
        if (Object.keys(body).length > 0) {
          const res = await fetch(`/api/organizer/events/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
          const j = await res.json().catch(() => null);
          if (!res.ok) {
            console.warn('[dashboard] updateEvent: server returned error', res.status, j);
          }
        }
      } catch (err) {
        console.error('[dashboard] updateEvent server save failed', err);
      }
    })();

    // Always update local UI state immediately for snappy UX
    setEvents(prev => {
      const next = prev.map(e => {
        if (e.id !== id) return e;
        const merged = { ...e, ...patch };
        try { console.log('[dashboard] updateEvent called', { id, recurring: merged.recurring, recurrenceLabel: merged.recurrenceLabel }); } catch (e) { /* ignore */ }
        return merged;
      });
      try { console.log('[dashboard] events after update:', next.map((m: any) => ({ id: m.id, recurring: m.recurring, recurrenceLabel: m.recurrenceLabel }))); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const addDeal = async (deal: Omit<Deal, 'id' | 'redeemed'>) => {
    try {
        const accountId = getAccountId();
      const accountEmail = getAccountEmail();
      const body: any = { ...deal };
      if (accountId) body.organizerId = accountId;
      else if (accountEmail) body.email = accountEmail;
      const res = await fetch('/api/organizer/deals', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await res.json().catch(() => null);
      if (res.ok && j && j.ok && j.deal) {
        const created = j.deal;
          setDeals(prev => [{ id: String(created._id || created.id), title: created.title || '', description: created.description || '', active: !!created.active, type: created.type || 'drink', redeemed: Number(created.redeemed || 0), startAt: created.startAt ? (new Date(created.startAt)).toISOString() : null, endAt: created.endAt ? (new Date(created.endAt)).toISOString() : null }, ...prev]);
        addNotification(`Deal "${created.title || ''}" erstellt.`);
        return;
      }
    } catch (e) { console.error('[dashboard] addDeal failed', e); }
    // fallback local add
    setDeals(prev => [{ ...deal, id: Math.random().toString(), redeemed: 0 }, ...prev]);
  };

  const toggleDeal = async (id: string) => {
    try {
      const target = deals.find(d => d.id === id);
      if (!target) return;
      const res = await fetch(`/api/organizer/deals/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !target.active }) });
      const j = await res.json().catch(() => null);
      if (res.ok && j && j.ok && j.deal) {
        const updated = j.deal;
        setDeals(prev => prev.map(d => d.id === id ? { id: String(updated._id || updated.id), title: updated.title || d.title, description: updated.description || d.description, active: !!updated.active, type: updated.type || d.type, redeemed: Number(updated.redeemed || d.redeemed) } : d));
        return;
      }
    } catch (e) { console.error('[dashboard] toggleDeal failed', e); }
    // fallback: local toggle
    setDeals(prev => prev.map(d => d.id === id ? { ...d, active: !d.active } : d));
  };

  const updateDeal = async (id: string, patch: Partial<Deal>) => {
    try {
      const body: any = { ...patch };
      const accountId = getAccountId();
      const accountEmail = getAccountEmail();
      if (accountId) body.organizerId = accountId; else if (accountEmail) body.email = accountEmail;
      const res = await fetch(`/api/organizer/deals/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await res.json().catch(() => null);
      if (res.ok && j && j.ok && j.deal) {
        const updated = j.deal;
        setDeals(prev => prev.map(d => d.id === id ? { id: String(updated._id || updated.id), title: updated.title || d.title, description: updated.description || d.description, active: !!updated.active, type: updated.type || d.type, redeemed: Number(updated.redeemed || d.redeemed), startAt: updated.startAt ? (new Date(updated.startAt)).toISOString() : null, endAt: updated.endAt ? (new Date(updated.endAt)).toISOString() : null } : d));
        return;
      }
    } catch (e) { console.error('[dashboard] updateDeal failed', e); }
    setDeals(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
  };

  const deleteDeal = async (id: string) => {
    try {
      const accountId = getAccountId();
      const accountEmail = getAccountEmail();
      const url = `/api/organizer/deals/${encodeURIComponent(id)}` + (accountId ? `?organizerId=${encodeURIComponent(accountId)}` : (accountEmail ? `?email=${encodeURIComponent(accountEmail)}` : ''));
      const res = await fetch(url, { method: 'DELETE' });
      const j = await res.json().catch(() => null);
      if (res.ok && j && j.ok) {
        setDeals(prev => prev.filter(d => d.id !== id));
        addNotification('Deal gelöscht.');
        return;
      }
    } catch (e) { console.error('[dashboard] deleteDeal failed', e); }
    // fallback: remove locally
    setDeals(prev => prev.filter(d => d.id !== id));
  };

  const acceptRide = () => {
    if (incomingRide) {
      setActiveRide({ ...incomingRide, status: 'active' });
      setIncomingRide(null);
      setDriverStatus('busy');
      addNotification('Fahrt angenommen. Navigation gestartet.');
    }
  };

  const completeRide = () => {
    setActiveRide(null);
    setDriverStatus('online');
    addNotification('Fahrt abgeschlossen. +14,50 €');
  };

  const addNotification = (msg: string) => {
    setNotifications(prev => [msg, ...prev]);
    // Simple alert for prototype
    alert(msg); 
  };

  // Listen for cross-window messages (used by Stripe onboarding popup to notify completion)
  const lastOnboardNotifyRef = React.useRef(0);
  React.useEffect(() => {
    const handler = async (e: any) => {
      try {
        if (!e || !e.data) return;
        // Only accept messages from our own origin (the Stripe return page).
        // Reject strictly: a missing/mismatched origin is not trusted.
        if (typeof window !== 'undefined' && e.origin !== window.location.origin) return;
        if (e.data.type !== 'stripe:onboard') return;
        // The refresh_url also posts this message with success:false — ignore it,
        // it is not a completion (it means the onboarding link needs a refresh).
        if (e.data.success !== true) return;
        // Dedupe: Stripe can deliver the return/redirect more than once.
        const now = Date.now();
        if (now - lastOnboardNotifyRef.current < 5000) return;
        lastOnboardNotifyRef.current = now;
        try { await fetchPulseProfile(); } catch (err) {}
        addNotification && addNotification('Stripe Onboarding abgeschlossen');
      } catch (err) { /* ignore */ }
    };
    if (typeof window !== 'undefined') window.addEventListener('message', handler);
    return () => { if (typeof window !== 'undefined') window.removeEventListener('message', handler); };
  }, []);

  return (
    <DashboardContext.Provider value={{
      events, eventsLoading, addEvent,
      archivedEvents,
      deals, addDeal, toggleDeal, updateDeal, deleteDeal,
      activeRide, incomingRide, driverStatus, setDriverStatus, acceptRide, completeRide,
      notifications, addNotification,
      locations, addLocation, updateLocation, removeLocation,
      removeEvent,
      updateEvent,
      pulsePosts, fetchPulsePosts, addPulsePost, deletePulsePost, pulseProfile, fetchPulseProfile, updatePulseProfile,
      models, currentModel, fetchModels, hasFeature
    }}>
      {children}
    </DashboardContext.Provider>
  );
};
